import { useState, useCallback } from 'react'; // v1.1
import { adminService } from '../services/adminService';
import apiService from '../services/api';
export const useAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fonction générique pour gérer les appels API
  const callService = useCallback(async (serviceFunction, ...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await serviceFunction(...args);
       // LOG AJOUTÉ
      
      // VÉRIFICATION IMPORTANTE : Si le résultat est déjà un objet {success, data}
      if (result && typeof result === 'object' && 'success' in result) {
        return result;
      }
      
      // Sinon, encapsuler dans un objet standard
      return { 
        success: true, 
        data: result,
        message: 'Success'
      };
    } catch (err) {
      console.error('❌ API Error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Une erreur est survenue';
      setError(errorMessage);
      return { success: false, message: errorMessage, data: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Méthodes spécifiques
  const getDashboard = useCallback(async () => {
    return callService(adminService.getDashboard);
  }, [callService]);

  const getUsers = useCallback(async (params) => {
    const result = await callService(adminService.getUsers, params);
    if (result.success) {
      // Retourner directement les données du serveur
      return result;
    }
    return result;
  }, [callService]);

  const updateUserRole = useCallback(async (userId, role) => {
    return callService(adminService.updateUserRole, userId, role);
  }, [callService]);

  const updateUserStatus = useCallback(async (userId, status) => {
    return callService(adminService.updateUserStatus, userId, status);
  }, [callService]);

  const getInvestmentPlans = useCallback(async () => {
    return callService(adminService.getInvestmentPlans);
  }, [callService]);

  const createInvestmentPlan = useCallback(async (planData) => {
    return callService(adminService.createInvestmentPlan, planData);
  }, [callService]);

  const updateInvestmentPlan = useCallback(async (planId, planData) => {
    return callService(adminService.updateInvestmentPlan, planId, planData);
  }, [callService]);

  const deleteInvestmentPlan = useCallback(async (planId) => {
    return callService(adminService.deleteInvestmentPlan, planId);
  }, [callService]);

  const updatePlanStatus = useCallback(async (planId, status) => {
    return callService(adminService.updatePlanStatus, planId, status);
  }, [callService]);

  const getMLMLevels = useCallback(async () => {
    const result = await callService(adminService.getMLMLevels);
    return result;
  }, [callService])

  const createMLMLevel = useCallback(async (levelData) => {
    return callService(adminService.createMLMLevel, levelData);
  }, [callService]);

  const updateMLMLevel = useCallback(async (levelId, levelData) => {
    return callService(adminService.updateMLMLevel, levelId, levelData);
  }, [callService]);

  const deleteMLMLevel = useCallback(async (levelId) => {
    return callService(adminService.deleteMLMLevel, levelId);
  }, [callService]);

  const getMLMRanks = useCallback(async () => {
    const result = await callService(adminService.getMLMRanks);
    return result;
  }, [callService]);

  const createMLMRank = useCallback(async (rankData) => {
    return callService(adminService.createMLMRank, rankData);
  }, [callService]);

  const updateMLMRank = useCallback(async (rankId, rankData) => {
    return callService(adminService.updateMLMRank, rankId, rankData);
  }, [callService]);

  const deleteMLMRank = useCallback(async (rankId) => {
    return callService(adminService.deleteMLMRank, rankId);
  }, [callService]);

  const getTransactions = useCallback(async (params) => {
    return callService(adminService.getTransactions, params);
  }, [callService]);

  const getTransactionStats = useCallback(async () => {
    return callService(adminService.getTransactionStats);
  }, [callService]);

  const updateTransactionStatus = useCallback(async (transactionId, status) => {
    return callService(adminService.updateTransactionStatus, transactionId, status);
  }, [callService]);

  const getFinancialStats = useCallback(async () => {
    return callService(adminService.getFinancialStats);
  }, [callService]);

  const getTradingStats = useCallback(async () => {
    return callService(async () => {
      const response = await apiService.get('/admin/dashboard/trading-stats');
      return response;
    });
  }, [callService]);

  const getHistoricalStats = useCallback(async () => {
    return callService(async () => {
      const response = await apiService.get('/admin/dashboard/growth-stats');
      return response;
    });
  }, [callService]);

  // Obtenir les détails d'un utilisateur
  const getUserDetails = useCallback(async (userId) => {
    return callService(async () => {
      try {
        const response = await apiService.get(`/admin/users/${userId}/details`);
        return response;
      } catch (error) {
        console.error('Erreur getUserDetails:', error);
        // Retourne des données par défaut si l'API échoue
        return {
          success: true,
          data: {
            id: userId,
            username: 'Utilisateur',
            email: 'email@example.com',
            role: 'user',
            account_status: 'active',
            kyc_status: 'pending',
            created_at: new Date().toISOString()
          }
        };
      }
    });
  }, [callService]);

  // Mettre à jour un utilisateur
  const updateUser = useCallback(async (userId, userData) => {
    return callService(async () => {
      try {
        const response = await apiService.put(`/admin/users/${userId}`, userData);
        return response;
      } catch (error) {
        console.error('Erreur updateUser:', error);
        return { 
          success: false, 
          message: error.message || 'Échec de la mise à jour' 
        };
      }
    });
  }, [callService]);

  // Réinitialiser le mot de passe d'un utilisateur
  const resetUserPassword = useCallback(async (userId) => {
    return callService(async () => {
      try {
        const response = await apiService.post(`/admin/users/${userId}/reset-password`);
        return response;
      } catch (error) {
        console.error('Erreur resetUserPassword:', error);
        return { 
          success: false, 
          message: error.message || 'Échec de la réinitialisation' 
        };
      }
    });
  }, [callService]);

  // Envoyer un email de vérification
  const sendVerificationEmail = useCallback(async (userId) => {
    return callService(async () => {
      try {
        const response = await apiService.post(`/admin/users/${userId}/send-verification`);
        return response;
      } catch (error) {
        console.error('Erreur sendVerificationEmail:', error);
        return { 
          success: false, 
          message: error.message || 'Échec de l\'envoi de l\'email' 
        };
      }
    });
  }, [callService]);

  // Obtenir les statistiques d'un utilisateur
  const getUserStats = useCallback(async (userId) => {
    return callService(async () => {
      try {
        const response = await apiService.get(`/admin/users/${userId}/stats`);
        return response;
      } catch (error) {
        console.error('Erreur getUserStats:', error);
        return {
          success: true,
          data: {
            total_invested: 0,
            total_commissions: 0,
            total_deposits: 0,
            total_withdrawals: 0,
            direct_referrals_count: 0,
            team_volume: 0,
            mlm_rank: 'No rank'
          }
        };
      }
    });
  }, [callService]);

  // Obtenir les logs d'un utilisateur
  const getUserLogs = useCallback(async (userId) => {
    return callService(async () => {
      try {
        const response = await apiService.get(`/admin/users/${userId}/logs`);
        return response;
      } catch (error) {
        console.error('Erreur getUserLogs:', error);
        return {
          success: true,
          data: []
        };
      }
    });
  }, [callService]);

  // Obtenir les informations de wallet
  const getUserWallet = useCallback(async (userId) => {
    return callService(async () => {
      try {
        const response = await apiService.get(`/admin/users/${userId}/wallet`);
        return response;
      } catch (error) {
        console.error('Erreur getUserWallet:', error);
        return {
          success: true,
          data: {
            wallets: [],
            recent_transactions: [],
            totals: { spot: 0, futures: 0, investment: 0 },
            total_balance: 0
          }
        };
      }
    });
  }, [callService]);

  // Obtenir les filleuls
  const getUserReferrals = useCallback(async (userId) => {
    return callService(async () => {
      try {
        const response = await apiService.get(`/admin/users/${userId}/referrals`);
        return response;
      } catch (error) {
        console.error('Erreur getUserReferrals:', error);
        return {
          success: true,
          data: {
            referrals: [],
            referrer: null,
            total_referrals: 0
          }
        };
      }
    });
  }, [callService]);

  const getWithdrawals = useCallback(async (params) => {
    return callService(async () => {
      const response = await apiService.get('/admin/withdrawals', { params });
      return response;
    });
  }, [callService]);

  const processWithdrawal = useCallback(async (withdrawalId, data) => {
    return callService(async () => {
      const response = await apiService.put(`/admin/withdrawals/${withdrawalId}/process`, data);
      return response;
    });
  }, [callService]);

  const verifyKYC = useCallback(async (userId, data) => {
    return callService(async () => {
      const response = await apiService.put(`/admin/kyc/verify/${userId}`, data);
      return response;
    });
  }, [callService]);

  const updateUserBalance = useCallback(async (userId, data) => {
    return callService(async () => {
      const response = await apiService.put(`/admin/users/${userId}/balance`, data);
      return response;
    });
  }, [callService]);

  // System Settings
  const getSystemSettings = useCallback(async () => {
    return callService(adminService.getSystemSettings);
  }, [callService]);

  const createSystemSetting = useCallback(async (settingData) => {
    return callService(adminService.createSystemSetting, settingData);
  }, [callService]);

  const updateSystemSettings = useCallback(async (settings) => {
    return callService(adminService.updateSystemSettings, settings);
  }, [callService]);

  const deleteSystemSetting = useCallback(async (id) => {
    return callService(adminService.deleteSystemSetting, id);
  }, [callService]);

  // Investment Types
  const getInvestmentTypes = useCallback(async () => {
    return callService(adminService.getInvestmentTypes);
  }, [callService]);

  const createInvestmentType = useCallback(async (typeData) => {
    return callService(adminService.createInvestmentType, typeData);
  }, [callService]);

  const updateInvestmentType = useCallback(async (id, typeData) => {
    return callService(adminService.updateInvestmentType, id, typeData);
  }, [callService]);

  const deleteInvestmentType = useCallback(async (id) => {
    return callService(adminService.deleteInvestmentType, id);
  }, [callService]);

  // Market Analyses
  const getAnalyses = useCallback(async () => {
    return callService(adminService.getAnalyses);
  }, [callService]);

  const createAnalysis = useCallback(async (analysisData) => {
    return callService(adminService.createAnalysis, analysisData);
  }, [callService]);

  const updateAnalysis = useCallback(async (id, analysisData) => {
    return callService(adminService.updateAnalysis, id, analysisData);
  }, [callService]);

  const deleteAnalysis = useCallback(async (id) => {
    return callService(adminService.deleteAnalysis, id);
  }, [callService]);

  // Audit Logs globaux
  const getGlobalAuditLogs = useCallback(async (params) => {
    return callService(async () => {
      const response = await apiService.get('/admin/audit-logs', { params });
      return response;
    });
  }, [callService]);


  return {
    loading,
    error,
    getDashboard,
    getUsers,
    updateUserRole,
    updateUserStatus,
    getInvestmentPlans,
    createInvestmentPlan,
    updateInvestmentPlan,
    deleteInvestmentPlan,
    updatePlanStatus,
    getMLMLevels,
    createMLMLevel,
    updateMLMLevel,
    deleteMLMLevel,
    getMLMRanks,
    createMLMRank,
    updateMLMRank,
    deleteMLMRank,
    getTransactions,
    getTransactionStats,
    updateTransactionStatus,
    getFinancialStats,
    getTradingStats,
    getHistoricalStats,
    getUserDetails,
    updateUser,
    resetUserPassword,
    sendVerificationEmail,
    getUserStats,
    getUserLogs,
    getUserWallet,
    getUserReferrals,
    getWithdrawals,
    processWithdrawal,
    verifyKYC,
    updateUserBalance,
    getSystemSettings,
    createSystemSetting,
    updateSystemSettings,
    deleteSystemSetting,
    getInvestmentTypes,
    createInvestmentType,
    updateInvestmentType,
    deleteInvestmentType,
    getAnalyses,
    createAnalysis,
    updateAnalysis,
    deleteAnalysis,
    getGlobalAuditLogs
  };

};